import { supabase } from '@/supabaseClient';
import { validateRequest, validateCreateRequest, validateUserRequest, validateSupportedRequest } from './validators';

const API_BASE = '/api';

/**
 * Handle authentication errors by signing out the user
 */
async function handleAuthError(error, response) {
  // Check if the error is related to authentication
  const isAuthError = 
    response?.status === 401 || 
    response?.status === 403 ||
    error.message?.includes('Token validation error') ||
    error.message?.includes('Token validation failed') ||
    error.message?.includes('Missing Authorization header') ||
    error.message?.includes('Invalid token') ||
    error.message?.includes('Auth session missing') ||
    error.message?.includes('Authentication failed');
    
  if (isAuthError) {
    console.warn('Authentication error detected, signing out user:', error.message);
    try {
      await supabase.auth.signOut();
    } catch (signOutError) {
      console.error('Error signing out user:', signOutError);
    }
  }
}

/**
 * Enhanced fetch wrapper with authentication error handling
 */
async function fetchWithAuthHandling(url, options = {}) {
  const response = await fetch(url, options);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    
    // Add additional properties from the error response
    if (errorData.requiresProfileSetup) {
      error.requiresProfileSetup = true;
    }
    
    // Handle authentication errors
    await handleAuthError(error, response);
    
    throw error;
  }
  
  return response;
}

/**
 * Requests API module
 * Handles all request-related API calls with proper validation
 */
export const api = {
  /**
   * Get all requests (authenticated users)
   */
  async getRequests(sortBy = 'newest', searchQuery = '') {
    console.log('API: Fetching requests (authenticated)', { sortBy, searchQuery });
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const params = new URLSearchParams();
      if (sortBy) params.append('sortBy', sortBy);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetchWithAuthHandling(`${API_BASE}/requests?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      // Validate each request
      return data.map((request, index) => 
        validateRequest(request, {
          actionName: 'getRequests',
          location: 'requests/api.js',
          direction: 'incoming',
          moduleFrom: 'backend',
          moduleTo: 'requests'
        })
      );
    } catch (error) {
      console.error('API: Error fetching requests:', error);
      throw error;
    }
  },

  /**
   * Get public requests (unauthenticated users)
   */
  async getPublicRequests(sortBy = 'newest', searchQuery = '') {
    console.log('API: Fetching public requests', { sortBy, searchQuery });
    
    try {
      const params = new URLSearchParams();
      if (sortBy) params.append('sortBy', sortBy);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetchWithAuthHandling(`${API_BASE}/public-requests?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      // Validate each request
      return data.map((request, index) => 
        validateRequest(request, {
          actionName: 'getPublicRequests',
          location: 'requests/api.js',
          direction: 'incoming',
          moduleFrom: 'backend',
          moduleTo: 'requests'
        })
      );
    } catch (error) {
      console.error('API: Error fetching public requests:', error);
      throw error;
    }
  },

  /**
   * Get user's own requests
   */
  async getMyRequests() {
    console.log('API: Fetching user\'s own requests');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/my-requests`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      // Validate each request
      return data.map((request, index) => 
        validateUserRequest(request, {
          actionName: 'getMyRequests',
          location: 'requests/api.js',
          direction: 'incoming',
          moduleFrom: 'backend',
          moduleTo: 'requests'
        })
      );
    } catch (error) {
      console.error('API: Error fetching user requests:', error);
      throw error;
    }
  },

  /**
   * Get requests the user has voted for
   */
  async getSupportedRequests() {
    console.log('API: Fetching user\'s voted requests');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/supported-requests`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      // Validate each request
      return data.map((request, index) => 
        validateSupportedRequest(request, {
          actionName: 'getSupportedRequests',
          location: 'requests/api.js',
          direction: 'incoming',
          moduleFrom: 'backend',
          moduleTo: 'requests'
        })
      );
    } catch (error) {
      console.error('API: Error fetching voted requests:', error);
      throw error;
    }
  },

  /**
   * Create a new request
   */
  async createRequest(requestData) {
    console.log('API: Creating new request', requestData);
    
    try {
      // Validate input data
      const validatedData = validateCreateRequest(requestData, {
        actionName: 'createRequest',
        location: 'requests/api.js',
        direction: 'outgoing',
        moduleFrom: 'requests',
        moduleTo: 'backend'
      });

      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/create-request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(validatedData),
      });

      const data = await response.json();
      
      // Validate response
      return validateRequest(data, {
        actionName: 'createRequest',
        location: 'requests/api.js',
        direction: 'incoming',
        moduleFrom: 'backend',
        moduleTo: 'requests'
      });
    } catch (error) {
      console.error('API: Error creating request:', error);
      throw error;
    }
  },

  /**
   * Toggle user's vote for a request
   */
  async toggleCommitment(requestId) {
    console.log('API: Toggling vote for request', requestId);
    
    try {
      if (!requestId) {
        throw new Error('Request ID is required');
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/toggle-commitment`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestId }),
      });

      const data = await response.json();
      console.log('API: Vote toggle successful', data);
      
      return data;
    } catch (error) {
      console.error('API: Error toggling vote:', error);
      throw error;
    }
  },

  /**
   * Update an existing request
   */
  async updateRequest(requestData) {
    console.log('API: Updating request', requestData);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/update-request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();
      console.log('API: Request update successful');
      
      return data;
    } catch (error) {
      console.error('API: Error updating request:', error);
      throw error;
    }
  },

  /**
   * Delete a request
   */
  async deleteRequest(requestId) {
    console.log('API: Deleting request', requestId);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/delete-request`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestId }),
      });

      const data = await response.json();
      console.log('API: Request deletion successful');
      
      return data;
    } catch (error) {
      console.error('API: Error deleting request:', error);
      throw error;
    }
  },

  /**
   * Get comments for a request
   */
  async getComments(requestId) {
    console.log('API: Fetching comments for request', requestId);
    
    try {
      if (!requestId) {
        throw new Error('Request ID is required');
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/comments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestId }),
      });

      const data = await response.json();
      console.log(`API: Found ${data.length} comments for request ${requestId}`);
      
      return data;
    } catch (error) {
      console.error('API: Error fetching comments:', error);
      throw error;
    }
  },

  /**
   * Add a comment to a request
   */
  async addComment(requestId, content) {
    console.log('API: Adding comment to request', { requestId, content });
    
    try {
      if (!requestId || !content) {
        throw new Error('Request ID and content are required');
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/add-comment`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestId, content }),
      });

      const data = await response.json();
      console.log('API: Comment added successfully', data);
      
      return data;
    } catch (error) {
      console.error('API: Error adding comment:', error);
      throw error;
    }
  },

  /**
   * Add a reply to a comment
   */
  async addReply(parentCommentId, content) {
    console.log('API: Adding reply to comment', { parentCommentId, content });
    
    try {
      if (!parentCommentId || !content) {
        throw new Error('Parent comment ID and content are required');
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/add-reply`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ parentCommentId, content }),
      });

      const data = await response.json();
      console.log('API: Reply added successfully', data);
      
      return data;
    } catch (error) {
      console.error('API: Error adding reply:', error);
      throw error;
    }
  },

  /**
   * Vote on a comment
   */
  async voteComment(commentId, voteType) {
    console.log('API: Voting on comment', { commentId, voteType });
    
    try {
      if (!commentId) {
        throw new Error('Comment ID is required');
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetchWithAuthHandling(`${API_BASE}/vote-comment`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ commentId, voteType }),
      });

      const data = await response.json();
      console.log('API: Comment vote successful', data);
      
      return data;
    } catch (error) {
      console.error('API: Error voting on comment:', error);
      throw error;
    }
  }
};